import type { ReactElement, ReactNode, RefObject } from 'react';
import { Children } from 'react';
import { useUID } from 'react-uid';

type StepProps = {
  title: ReactNode;
  hidden?: boolean;
  activeSection: ReactNode;
  completedSection: ReactNode;
  isActive: boolean;
  isCompleted: boolean;
  setActive: (isActive: boolean) => void;
  setCompleted: (isCompleted: boolean) => void;
  hideChangeButton?: boolean;
  stepRef?: RefObject<HTMLDivElement>;
};

type StepChild = { props: StepProps } | false;

/* eslint-disable @typescript-eslint/no-unused-vars */
function Step({
  title,
  hidden = false,
  activeSection,
  completedSection,
  isActive,
  isCompleted,
  setActive,
  setCompleted,
  hideChangeButton = false,
  stepRef,
}: StepProps): ReactElement {
  // @ts-expect-error TODO: fix null safety
  return null;
}
/* eslint-enable @typescript-eslint/no-unused-vars */

type Props = {
  stepByStep?: boolean;
  children: StepChild[],
};

function Accordion({ stepByStep = false, children }: Props): ReactElement {
  const uid = useUID();
  const filteredChildren = children.filter((child) => !!child);

  const steps = Children.map(filteredChildren, (child) => {
    if ((child as ReactElement).type !== Step) {
      throw new Error('Children of Accordion component must be of type Step');
    }
    return child;
  });

  const onChange = (step) => {
    steps.forEach((item) => item.props.setActive(false));
    step.props.setActive(true);
  };

  const onActivate = (step, i) => {
    if (!step.props.isCompleted && (!stepByStep || (stepByStep && (
      (i > 0 && steps[i - 1].props.isCompleted) || (i === 0)
    )))) {
      onChange(step);
    }
  };

  return (
    <>
      {steps.map((step, i) => !step.props.hidden && (
        <div
          ref={step.props.stepRef}
          key={
            typeof step.props.title === 'string'
              ? step.props.title
              : uid
          }
          className={[
            'c-accordion-step',
            step.props.isActive ? 'is-active' : '',
            !step.props.isActive && step.props.isCompleted
              ? 'is-completed'
              : '',
            stepByStep && i > 0 && !steps[i - 1].props.isCompleted
              ? 'is-inactive'
              : '',
          ].join(' ')}
        >
          <div className="c-accordion-step__content">
            <div className="c-accordion-step__header">
              <h2 className="c-heading c-heading--brand-7">
                <span
                  role="option"
                  aria-selected={step.props.isActive}
                  onClick={() => onActivate(step, i)}
                  onKeyPress={(e) => e.key === 'Enter' && onActivate(step, i)}
                  tabIndex={0}
                >
                  {step.props.title}
                  <span className="sr-only"> section</span>
                </span>

                {!step.props.hideChangeButton
                && !step.props.isActive
                && step.props.isCompleted && (
                  <span className="u-text--md">
                    <button
                      type="button"
                      className="c-link"
                      onClick={() => onChange(step)}
                    >
                      Change
                    </button>
                  </span>
                )}
              </h2>
            </div>

            <div className="c-accordion-step__section-active">
              <div className="c-accordion-step__body">
                {step.props.activeSection}
              </div>
            </div>

            <div className="c-accordion-step__section-completed">
              <div className="c-accordion-step__body">
                {step.props.completedSection}
              </div>
            </div>
          </div>
        </div>
      ))}
    </>
  );
}

Accordion.Step = Step;

export default Accordion;
